import { put, takeEvery, call } from "redux-saga/effects"
import {
  GET_PROFILE,
  UPDATE_PROFILE,
  GET_ROLES,
  UPDATE_PROFILE_META_TAGS,
  CHANGE_PASSWORD,
  GET_EVENT_TEASER,
  DELETE_ADMIN_USER,
  CREATE_ADMIN_USER,
  UPDATE_ADMIN_USER,
  GET_USER_PROFILE,
  PWD_SECURITY_CHECK,
} from "store/profile/actionTypes"
import {
  apiError,
  createAdminUserFailed,
  createAdminUserSuccess,
  deleteAdminUserFailed,
  deleteAdminUserSuccess,
  getEventTeaserSuccess,
  getProfileSuccess,
  getRolesSuccess,
  getUserProfileFailed,
  getUserProfileSuccess,
  pwdSecurityCheckFailed,
  pwdSecurityCheckSuccess,
  updateAdminUserFailed,
  updateAdminUserSuccess,
  updateProfileSuccess,
} from "store/profile/actions"
import { post, get, axiosPut, del } from "helpers/api_helper"
import * as url from "helpers/url_helper"
import { getApiErrorMessage, getFormDataFromJSON, showToast } from "utils"
import { LOCAL_STORAGE_TAGS } from "constants/index"
import {
  changePasswordFailed,
  changePasswordSuccess,
  updateAuthUserData,
  updateProfileMetaTagsSuccess,
} from "store/actions"

function* getProfileSaga({ payload: { params } }) {
  try {
    const response = yield get(`${url.USER_URL}/${params.user_id}`, { params })
    yield put(getProfileSuccess(response))
  } catch (error) {
    yield put(apiError(error))
  }
}

function* updateProfileSaga({ payload: { values } }) {
  let formData = getFormDataFromJSON(values?.values)
  try {
    let callUrl = url.USER_URL
    if (values?.values.type > 0) {
      callUrl = url.ADMIN_USER_URL
    }
    const response = yield call(() =>
      post(`${callUrl}/${values?.values.id}`, formData)
    )
    if (values?.isOwnProfile) {
      yield put(
        updateAuthUserData({
          ...values?.authUser,
          first_name: response?.first_name,
          last_name: response?.last_name,
          email: response?.email,
          profile_pic: response?.profile_pic,
        })
      )
      yield localStorage.setItem(
        LOCAL_STORAGE_TAGS.AUTH_USER,
        JSON.stringify({
          ...values?.authUser,
          first_name: response?.first_name,
          last_name: response?.last_name,
          email: response?.email,
          profile_pic: response?.profile_pic,
        })
      )
    }
    yield put(updateProfileSuccess(response))
    showToast("success", "Profile updated!", "Success")
  } catch ({ response }) {
    const errors = response?.data?.error 
    let message = "Please try again"
    if (typeof errors === "string") {
      message = errors
    } else if (typeof errors === "object") {
      message = Object.entries(errors)
        ?.map(([key, value]) => {
          const errorMessage = Array.isArray(value) ? value?.join(", ") : value
          return `${key}: ${errorMessage}`
        })
        .join("; ")
    }
    showToast("error", message, "Error")
    yield put(apiError(message))
  }
}

function* updateProfileTags({ payload }) {
  try {
    yield call(() =>
      post(url.USER_META_TAGS, {
        ...payload,
        tags: payload?.tags?.map(tag => tag.id) || [],
      })
    )
    yield put(updateProfileMetaTagsSuccess(payload))
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getRoles({ payload }) {
  try {
    const roles = yield get(url.GET_ADMIN_ROLES)
    yield put(getRolesSuccess(roles))
  } catch (error) {
    yield put(apiError(error))
  }
}

function* changePassword({ payload: params }) {
  try {
    const respose = yield axiosPut(
      `/users/${params?.userId}/password`,
      params?.passwordsData
    )
    yield put(changePasswordSuccess(respose))
    showToast("Success", "Password changed!", "Success")
  } catch (error) {
    yield put(changePasswordFailed(error))
  }
}

function* getEventTeaser({ payload: params }) {
  try {
    const response = yield get(`/admin/user/${params?.userId}/event/teaser`)
    yield put(getEventTeaserSuccess(response))
  } catch (error) {
    yield put(apiError(error))
  }
}

function* deleteAdminUser({ payload: params }) {
  try {
    const response = yield del(
      `/admin/${params?.type === 1 ? "business" : "corporate"}/${
        params?.type === 1 ? params?.buId : params?.vendorId
      }/user/${params?.userId}`
    )
    yield put(deleteAdminUserSuccess(response))
    showToast("success", "Admin Deleted", "Success")
  } catch (error) {
    yield put(deleteAdminUserFailed(error))
    showToast("error", error, "Error")
  }
}

function* createAdminUser({ payload: params }) {
  try {
    const formData = getFormDataFromJSON(params)
    const response = yield post(url.ADMIN_USER_URL, formData)
    yield put(createAdminUserSuccess(response))
    showToast("success", "Admin User Saved!", "Success")
  } catch ({ response }) {
    const message = getApiErrorMessage(response, false)
    showToast("error", message, "Error")
    yield put(createAdminUserFailed(message))
  }
}

function* updateAdminUser({ payload: params }) {
  try {
    const formData = getFormDataFromJSON(params?.data)
    const response = yield post(
      url.ADMIN_USER_URL + "/" + params?.userId,
      formData
    )
    yield put(updateAdminUserSuccess(response))
    showToast("success", "Admin User Saved!", "Success")
  } catch (error) {
    yield put(updateAdminUserFailed(error))
    showToast("error", error, "Error")
  }
}

function* getUserProfile({ payload: params }) {
  try {
    const response = yield get(url.GET_USER_PROFILE_URL, {
      headers: {
        ["Authorization"]: `Bearer ${params?.token}`,
      },
    })
    yield put(getUserProfileSuccess(response))
  } catch (error) {
    yield put(getUserProfileFailed(error))
    showToast("error", error, "Error")
  }
}

function* pwdSecurityCheck({ payload: params }) {
  try {
    const response = yield post("/user/password/verify", params)
    yield put(pwdSecurityCheckSuccess(response))
    if (!response?.is_correct) {
      showToast("error", "Incorrect password", "Error")
    }
  } catch (error) {
    yield put(pwdSecurityCheckFailed(error))
    showToast("error", error, "Error")
  }
}

function* profileSaga() {
  yield takeEvery(GET_PROFILE, getProfileSaga)
  yield takeEvery(UPDATE_PROFILE, updateProfileSaga)
  yield takeEvery(GET_ROLES, getRoles)
  yield takeEvery(UPDATE_PROFILE_META_TAGS, updateProfileTags)
  yield takeEvery(CHANGE_PASSWORD, changePassword)
  yield takeEvery(GET_EVENT_TEASER, getEventTeaser)
  yield takeEvery(DELETE_ADMIN_USER, deleteAdminUser)
  yield takeEvery(CREATE_ADMIN_USER, createAdminUser)
  yield takeEvery(UPDATE_ADMIN_USER, updateAdminUser)
  yield takeEvery(GET_USER_PROFILE, getUserProfile)
  yield takeEvery(PWD_SECURITY_CHECK, pwdSecurityCheck)
}

export default profileSaga
